import {
  Avatar,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button
} from '@mui/material';
import SocialMediaIcon from 'src/components/SocialMediaIcon';
import MediaSection from './components/MediaSection';
import useQueryUrl from 'src/hooks/useQuery';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { Link } from 'react-router-dom';
import EmptyImageSection from '../../../../assets/image/empty_section.svg';
import useGetDetailNewsLetter from './hooks/useGetDetailNewsLetter';
import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import moment from 'moment';
import { resetTranslation } from 'src/features/translateArticle';

export const OnLoading = () => {
  return (
    <Stack spacing={1} width={'100%'} p={'2%'}>
      {/* For other variants, adjust the size with `width` and `height` */}

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 10 }}>
        <Skeleton variant="circular" width={100} height={100} />
        <Skeleton variant="rectangular" width={'20%'} height={60} />
      </Stack>
      <Stack direction="row" justifyContent="center">
        <Skeleton variant="rectangular" width={'50%'} height={50} />
      </Stack>

      <Skeleton variant="rectangular" width={'100%'} height={300} />

      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
    </Stack>
  );
};

// Tambahkan komponen ini di atas NewsLetterPreview
const EmptyContent = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      py: 8,
      px: 4,
      textAlign: 'center',
      minHeight: '50vh'
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        backgroundColor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <NewspaperIcon sx={{ width: 40, height: 40, color: 'text.disabled' }} />
    </Box>
    <Typography variant="h6" fontWeight={600} color="text.primary">
      No content available
    </Typography>
  </Box>
);

// Component untuk artikel tidak ditemukan
const NewsletterNotFound = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Card
        elevation={3}
        sx={{
          borderRadius: 3,
          textAlign: 'center',
          padding: 4,
          maxWidth: '40%',
          width: '100%'
        }}
      >
        <CardContent>
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <NewspaperIcon sx={{ height: 50, width: 50, color: 'gray' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}
          >
            Newsletter Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            Sorry, the Newsletter article you're looking for isn't available at
            this time. It may have been deleted or unpublished.
          </Typography>

          {/* Action Button */}
          <Button
            variant="contained"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={handleGoBack}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              boxShadow: 2
            }}
          >
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export const NewsLetterPreview = () => {
  const query = useQueryUrl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const newsLetterId = query.get('id');
  const { newsLetter, newsLetterLoading } = useGetDetailNewsLetter();

  const printMediaSectionRef = React.useRef<HTMLDivElement>(null);
  const broadcastSectionRef = React.useRef<HTMLDivElement>(null);
  const onlineMediaSectionRef = React.useRef<HTMLDivElement>(null);
  const sosmedSectionRef = React.useRef<HTMLDivElement>(null);

  const {
    isLogo,
    logoUrl,
    headerImageUrl,
    primaryText,
    secondaryText,
    mainColor,
    fontColor,
    titleColor,
    footerBackgroudColor,
    footerText,
    isFooterSocialMedia,
    instagram,
    facebook,
    twitter,
    tiktok,
    youtube
  } = useAppSelector(
    (state) => state.storeCustomNewsLetter.selectedNewsLetter.customizeTemplate
  );

  const { selectedNewsLetter } = useAppSelector(
    (state) => state.storeCustomNewsLetter
  );

  const {
    selectedStreamSosmedList,
    selectedStreamOnlineMediaList,
    selectedStreamBroadcastList,
    selectedStreamPrintMediaList
  } = selectedNewsLetter.selectDataSource;

  // Helper: cek apakah semua section kosong
  const isAllSectionEmpty =
    !selectedStreamPrintMediaList?.length &&
    !selectedStreamBroadcastList?.length &&
    !selectedStreamOnlineMediaList?.length &&
    !selectedStreamSosmedList?.length;

  // ..

  const { deliveryDate } = selectedNewsLetter.emailSetting;

  React.useEffect(() => {
    if (!newsLetterId) {
      navigate('/');
    }
  }, [newsLetterId]);

  React.useEffect(() => {
    return () => {
      dispatch(resetTranslation());
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#F5F5F5'
        }}
      >
        {newsLetterLoading ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              maxWidth: { xs: '100%', md: '50%' },
              backgroundColor: '#FFFFFFFF',
              overflowY: 'scroll',
              margin: 0
            }}
          >
            {' '}
            <OnLoading />
          </Box>
        ) : !newsLetter ? (
          <NewsletterNotFound />
        ) : (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              maxWidth: { xs: '100%', md: '50%' },
              backgroundColor: '#FFFFFFFF',
              overflowY: 'scroll',
              margin: 0
            }}
          >
            <>
              {/**Header */}
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems="center"
                height={150}
                sx={{ px: 4, py: 2 }}
              >
                <Box
                  sx={{
                    width: 150,
                    height: 70,
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {logoUrl && (
                    <img
                      style={{
                        objectFit: 'contain',
                        width: '100%',
                        height: '100%'
                      }}
                      src={isLogo && logoUrl ? logoUrl : EmptyImageSection}
                      onError={(e) => (e.currentTarget.src = EmptyImageSection)}
                      alt="logo"
                    />
                  )}
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={600}
                  fontSize={{ xs: 10, lg: 12 }}
                  textAlign={'right'}
                >
                  {deliveryDate
                    ? moment(deliveryDate).format('ddd, MMMM D YYYY h:mm ')
                    : 'Date Schedule'}
                </Typography>
              </Stack>

              {/**Title */}
              <Stack
                direction={'row'}
                justifyContent="center"
                alignItems="center"
                sx={{ px: 4, py: 2 }}
              >
                <Typography
                  variant="h2"
                  color={titleColor ? titleColor : '#000000'}
                  fontWeight={600}
                >
                  {primaryText}
                </Typography>
              </Stack>

              {/**Header image */}
              {headerImageUrl && (
                <Stack
                  direction={'row'}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ overflow: 'hidden', height: 250 }}
                >
                  <img
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                    }}
                    src={headerImageUrl || EmptyImageSection}
                    alt="logo"
                    onError={(e) => (e.currentTarget.src = EmptyImageSection)}
                  />
                </Stack>
              )}

              {/**Second text */}
              {secondaryText && (
                <Stack
                  direction={'row'}
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    py: 2,
                    background: mainColor ? mainColor : '#0B85AE',
                    minHeight: 70
                  }}
                >
                  <Typography
                    variant="body1"
                    px={'20%'}
                    textAlign="center"
                    color={fontColor ? fontColor : '#FEFEFEFF'}
                    fontWeight={500}
                  >
                    {secondaryText}
                  </Typography>
                </Stack>
              )}

              {/**Section content */}
              <Stack direction={'column'} spacing={4} p={{ xs: 1, md: 4 }}>
                {isAllSectionEmpty ? (
                  <EmptyContent />
                ) : (
                  <>
                    <MediaSection
                      ref={printMediaSectionRef}
                      label="Print Media"
                      mainColor={mainColor}
                      fontColor={fontColor}
                      mediaType="print_media"
                      items={selectedStreamPrintMediaList}
                      previewOnly
                    />

                    <MediaSection
                      ref={broadcastSectionRef}
                      label="Broadcast"
                      mainColor={mainColor}
                      fontColor={fontColor}
                      mediaType="broadcast"
                      items={selectedStreamBroadcastList}
                      previewOnly
                    />

                    <MediaSection
                      ref={onlineMediaSectionRef}
                      label="Online News"
                      mainColor={mainColor}
                      fontColor={fontColor}
                      mediaType="online_media"
                      items={selectedStreamOnlineMediaList}
                      previewOnly
                    />

                    <MediaSection
                      ref={sosmedSectionRef}
                      label="Social Media"
                      mainColor={mainColor}
                      fontColor={fontColor}
                      mediaType="social_media"
                      items={selectedStreamSosmedList}
                      previewOnly
                    />
                  </>
                )}
              </Stack>

              {/**Footer */}
              <Box
                sx={{
                  background: footerBackgroudColor
                    ? footerBackgroudColor
                    : '#0B85AE',
                  minHeight: 50,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2
                }}
              >
                <Typography
                  variant="h4"
                  px={'20%'}
                  py={2}
                  textAlign="center"
                  color={fontColor ? fontColor : '#FEFEFEFF'}
                  fontWeight={700}
                >
                  {footerText || ''}
                </Typography>
                {isFooterSocialMedia && (
                  <>
                    <Typography
                      variant="h6"
                      px={'20%'}
                      textAlign="center"
                      color={fontColor ? fontColor : '#FEFEFEFF'}
                      fontWeight={700}
                    >
                      Follow us on Social Media
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      my={1}
                    >
                      {twitter && (
                        <Link to={twitter}>
                          <SocialMediaIcon
                            socialMedia="twitter"
                            sx={{ color: 'white' }}
                          />
                        </Link>
                      )}
                      {tiktok && (
                        <Link to={tiktok}>
                          <SocialMediaIcon
                            socialMedia="tiktok"
                            sx={{ color: 'white' }}
                          />
                        </Link>
                      )}
                      {youtube && (
                        <Link to={youtube}>
                          <SocialMediaIcon
                            socialMedia="youtube"
                            sx={{ color: 'white' }}
                          />
                        </Link>
                      )}
                      {facebook && (
                        <Link to={facebook}>
                          <SocialMediaIcon
                            socialMedia="facebook"
                            sx={{ color: 'white' }}
                          />
                        </Link>
                      )}
                      {instagram && (
                        <Link to={instagram}>
                          <SocialMediaIcon
                            socialMedia="instagram"
                            sx={{ color: 'white' }}
                          />
                        </Link>
                      )}
                    </Stack>
                  </>
                )}
              </Box>
            </>
          </Box>
        )}

        {/* )} */}
      </Box>
    </>
  );
};

export default NewsLetterPreview;

// const TestingComponent = () => {
//   return (
//     <Box
//       sx={{
//         height: '100vh',
//         width: '100%',
//         backgroundColor: 'red',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center'
//       }}
//     >
//       <Box
//         sx={{
//           height: '100%',
//           width: '100%',
//           maxWidth: '500px',
//           backgroundColor: 'green',
//           overflow: 'scroll'
//         }}
//       >
//         {Array.from({ length: 100 }, (_, index) => {
//           return (
//             <Box
//               sx={{
//                 border: '1px solid black'
//               }}
//             >
//               <h1>test</h1>
//             </Box>
//           );
//         })}
//       </Box>
//     </Box>
//   );
// };
